import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { Toaster } from './components/ui/sonner';
import SubscriptionBlockedModal from './components/SubscriptionBlockedModal';
import WebSocketNotifications from './components/WebSocketNotifications';
import { Sentry } from './utils/sentry';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import Dashboard from './pages/Dashboard';
import ClientsPage from './pages/ClientsPage';
import ParticipantsPage from './pages/ParticipantsPage';
import ParticipantProfilePage from './pages/ParticipantProfilePage';
import DeviationsPage from './pages/DeviationsPage';
import FollowUpsPage from './pages/FollowUpsPage';
import GoalsPage from './pages/GoalsPage';
import AttendancePage from './pages/AttendancePage';
import MessagesPage from './pages/MessagesPage';
import ReportsPage from './pages/ReportsPage';
import AIPlansPage from './pages/AIPlansPage';
import AssessmentsPage from './pages/AssessmentsPage';
import UsersAdminPage from './pages/UsersAdminPage';
import MeetingNotesPage from './pages/MeetingNotesPage';
import OCNValidationPage from './pages/OCNValidationPage';
import SubscriptionPage from './pages/SubscriptionPage';
import ProfilePage from './pages/ProfilePage';
import AIUsagePage from './pages/AIUsagePage';
import TagsPage from './pages/TagsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import VerksamheterPage from './pages/VerksamheterPage';
import ContractsPage from './pages/admin/ContractsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import SupportSessionsPage from './pages/SupportSessionsPage';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailsPage from './pages/CustomerDetailsPage';
import SubscriptionPlansPage from './pages/SubscriptionPlansPage';
import PlatformAnalyticsPage from './pages/PlatformAnalyticsPage';
import InvoicesAdminPage from './pages/admin/InvoicesAdminPage';
import InvoicesPage from './pages/InvoicesPage';

import './App.css';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-muted-foreground">Laddar...</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" />;
  if (user.force_password_change && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" />;
  }
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-muted-foreground">Laddar...</p>
      </div>
    );
  }
  return user ? <Navigate to="/dashboard" /> : children;
};

const RoleRoute = ({ children, allowed }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-muted-foreground">Laddar...</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" />;
  if (!allowed.includes(user.role)) return <Navigate to="/dashboard" />;
  return children;
};

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<div className="min-h-screen flex items-center justify-center"><p className="text-muted-foreground">Något gick fel. Ladda om sidan.</p></div>}>
      <AuthProvider>
        <WebSocketProvider>
        <BrowserRouter>
          <SubscriptionBlockedModal />
          <WebSocketNotifications />
          <Routes>
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
          <Route path="/change-password" element={<PrivateRoute><ChangePasswordPage /></PrivateRoute>} />
          <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/clients" element={<RoleRoute allowed={['super_admin']}><ClientsPage /></RoleRoute>} />
          <Route path="/agreements" element={<Navigate to="/admin/contracts" />} />
          <Route path="/participants" element={<RoleRoute allowed={['super_admin', 'customer_admin', 'employee', 'read_only_assigner']}><ParticipantsPage /></RoleRoute>} />
          <Route path="/participants/:id" element={<RoleRoute allowed={['super_admin', 'customer_admin', 'employee', 'read_only_assigner']}><ParticipantProfilePage /></RoleRoute>} />
          <Route path="/deviations" element={<RoleRoute allowed={['super_admin', 'customer_admin', 'employee']}><DeviationsPage /></RoleRoute>} />
          <Route path="/follow-ups" element={<RoleRoute allowed={['super_admin', 'customer_admin', 'employee', 'read_only_assigner']}><FollowUpsPage /></RoleRoute>} />
          <Route path="/goals" element={<RoleRoute allowed={['super_admin', 'customer_admin', 'employee']}><GoalsPage /></RoleRoute>} />
          <Route path="/attendance" element={<RoleRoute allowed={['super_admin', 'customer_admin', 'employee']}><AttendancePage /></RoleRoute>} />
          <Route path="/meeting-notes" element={<RoleRoute allowed={['super_admin', 'customer_admin', 'employee']}><MeetingNotesPage /></RoleRoute>} />
          <Route path="/messages" element={<PrivateRoute><MessagesPage /></PrivateRoute>} />
          <Route path="/reports" element={<RoleRoute allowed={['super_admin', 'customer_admin', 'read_only_assigner', 'read_only_participant']}><ReportsPage /></RoleRoute>} />
          <Route path="/ai-plans" element={<RoleRoute allowed={['super_admin', 'customer_admin']}><AIPlansPage /></RoleRoute>} />
          <Route path="/assessments" element={<RoleRoute allowed={['super_admin', 'customer_admin', 'employee']}><AssessmentsPage /></RoleRoute>} />
          <Route path="/ocn-validations" element={<RoleRoute allowed={['super_admin', 'customer_admin', 'employee']}><OCNValidationPage /></RoleRoute>} />
          <Route path="/users-admin" element={<RoleRoute allowed={['super_admin', 'customer_admin']}><UsersAdminPage /></RoleRoute>} />
          <Route path="/settings/subscription" element={<RoleRoute allowed={['super_admin', 'customer_admin']}><SubscriptionPage /></RoleRoute>} />
          <Route path="/settings/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
          <Route path="/admin/ai-usage" element={<RoleRoute allowed={['super_admin']}><AIUsagePage /></RoleRoute>} />
          <Route path="/tags" element={<RoleRoute allowed={['super_admin', 'customer_admin', 'employee']}><TagsPage /></RoleRoute>} />
          <Route path="/analytics" element={<RoleRoute allowed={['super_admin', 'customer_admin', 'read_only_assigner']}><AnalyticsPage /></RoleRoute>} />
          <Route path="/verksamheter" element={<RoleRoute allowed={['super_admin', 'customer_admin']}><VerksamheterPage /></RoleRoute>} />
          <Route path="/admin/contracts" element={<RoleRoute allowed={['super_admin']}><ContractsPage /></RoleRoute>} />
          <Route path="/admin/contracts/new" element={<RoleRoute allowed={['super_admin']}><ContractsPage /></RoleRoute>} />
          <Route path="/admin/support-sessions" element={<RoleRoute allowed={['super_admin']}><SupportSessionsPage /></RoleRoute>} />
          <Route path="/admin/customers" element={<RoleRoute allowed={['super_admin']}><CustomersPage /></RoleRoute>} />
          <Route path="/admin/customers/:id" element={<RoleRoute allowed={['super_admin']}><CustomerDetailsPage /></RoleRoute>} />
          <Route path="/admin/subscription-plans" element={<RoleRoute allowed={['super_admin']}><SubscriptionPlansPage /></RoleRoute>} />
          <Route path="/admin/platform-analytics" element={<RoleRoute allowed={['super_admin']}><PlatformAnalyticsPage /></RoleRoute>} />
          <Route path="/admin/invoices" element={<RoleRoute allowed={['super_admin']}><InvoicesAdminPage /></RoleRoute>} />
          <Route path="/invoices" element={<RoleRoute allowed={['customer_admin']}><InvoicesPage /></RoleRoute>} />
            <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
          <Toaster position="top-right" />
        </BrowserRouter>
        </WebSocketProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
